import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { noop, theme, VStack } from '@sweetgreen/garnish';

import { LoadingAnimation } from '@order/components';
import { useCustomer } from '@order/Customer';
import { useTrackEffect } from '@order/Telemetry';

import { TransparentModal } from '../../../components/TransparentModal';
import { Footer, UsageHint } from '../ScanToPay.components';
import { useScanToPayFeatureFlag } from '../ScanToPay.featureFlag';
import { useScanToPayNavigation } from '../ScanToPay.navigation';
import { ScanToPayQrCode } from '../ScanToPay.qrcode';
import { CreditSelector } from '../Selectors/ScanToPay.credit';
import { GiftCodeSelector } from '../Selectors/ScanToPay.giftCode';
import { PaymentMethodSelector } from '../Selectors/ScanToPay.payments';

export const ScanToPayOptions = () => {
  const { goToPayments, goToGiftCode, goBack, handleSelectAddPaymentMethod } =
    useScanToPayNavigation();

  useTrackEffect('scan-to-pay.view');

  // ─── disable feature if missing qrcode or feature flag is off  ──────────
  const { customer, isCustomerLoaded } = useCustomer();
  const isScanToPayEnabled = useScanToPayFeatureFlag();
  const canCustomerScanToPay = Boolean(customer.qrCode);
  const shouldDisableScanToPay = !isScanToPayEnabled || !canCustomerScanToPay;
  const shouldRedirectBack = isCustomerLoaded && shouldDisableScanToPay;

  // ─── redirect to payments if customer has no billing accounts  ──────────
  const hasBillingAccounts = (customer.billingAccounts ?? []).length > 0;
  const shouldRedirectToPayments = isCustomerLoaded && !hasBillingAccounts;

  // ─── automatic redirect  ────────────────────────────────────────────────
  React.useLayoutEffect(() => {
    if (shouldRedirectBack) goBack();
    else if (shouldRedirectToPayments) handleSelectAddPaymentMethod();
  }, [
    shouldRedirectBack,
    shouldRedirectToPayments,
    goBack,
    handleSelectAddPaymentMethod,
  ]);

  if (shouldDisableScanToPay) return null;

  if (!customer?.qrCode) return <LoadingAnimation />;

  return (
    <TransparentModal size="full" onClose={noop}>
      <View testID="scan-to-pay.options.route" style={styles.column}>
        <ScanToPayQrCode value={customer.qrCode} />

        <View style={styles.contentContainer}>
          <ScrollView contentContainerStyle={styles.scrollContainer}>
            <VStack
              customSpacing={0}
              style={styles.stackContainer}
              divider={true}
              dividerColor={theme.colors.LIGHT}
            >
              <UsageHint />
              <PaymentMethodSelector onSelect={goToPayments} />
              <CreditSelector />
              <GiftCodeSelector onSelect={goToGiftCode} />
            </VStack>
          </ScrollView>
        </View>

        <View style={styles.footerContainer}>
          <Footer />
        </View>
      </View>
    </TransparentModal>
  );
};

const styles = StyleSheet.create({
  column: {
    flex: 1,
    backgroundColor: theme.colors.KHAKI_3,
  },
  contentContainer: {
    flex: 1,
    paddingTop: theme.spacing.medium,
  },
  footerContainer: {
    paddingTop: theme.spacing.medium,
  },
  scrollContainer: {
    flexGrow: 1,
  },
  stackContainer: {
    paddingHorizontal: theme.spacing.small,
  },
});
